import React from 'react';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { getPrivacyPolicySchema, getBreadcrumbSchema } from '../utils/structuredData';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  // Breadcrumb data for privacy policy page
  const breadcrumbData = [
    { name: 'Home', url: '/' },
    { name: 'Privacy Policy', url: '/#privacy' }
  ];

  // Combined structured data for privacy policy
  const combinedStructuredData = [
    getPrivacyPolicySchema(),
    getBreadcrumbSchema(breadcrumbData)
  ];

  return (
    <div className="privacy-policy-page">
      <SEO
        title="Privacy Policy - Utility Tools PDF Converter"
        description="Privacy Policy for Utility Tools PDF Converter - Learn how we protect your data and privacy in compliance with Indian privacy laws including DPDP Act 2023 and IT Act 2000."
        keywords="privacy policy, data protection, DPDP Act 2023, IT Act 2000, user privacy, data security, PDF converter privacy, utility tools privacy"
        url="/#privacy"
        type="article"
        canonical={`${window.location.origin}/#privacy`}
        structuredData={combinedStructuredData}
      />

      <Header />

      <main className="privacy-policy-main">
        <div className="landing-container">
          <div className="privacy-policy-header">
            <h1 className="privacy-policy-title">Privacy Policy</h1>
            <div className="privacy-policy-meta">
              <p><strong>Effective Date:</strong> Sep 10, 2025</p>
              <p><strong>Last Updated:</strong> Sep 10, 2025</p>
            </div>
          </div>

          <div className="privacy-policy-content">

            <section className="privacy-section">
              <h2>1. Introduction</h2>
              <p>
                This Privacy Policy describes how UtilityTools PDF Converter ("we", "our", "us", or "the App") handles 
                information in compliance with Indian privacy laws, including the Digital Personal Data Protection Act 
                (DPDP) 2023 and the Information Technology Act 2000.
              </p>
              <p>By using this App, you agree to the practices described in this policy.</p>
            </section>

            <section className="privacy-section">
              <h2>2. Data Processing Principles</h2>
              <p>In accordance with DPDP Act 2023, we follow these principles:</p>
              <ul className="privacy-list">
                <li><strong>Lawfulness and Transparency:</strong> All data processing is lawful and transparent</li>
                <li><strong>Purpose Limitation:</strong> Data is processed only for specified purposes</li>
                <li><strong>Data Minimization:</strong> We process minimal data necessary for functionality</li>
                <li><strong>Storage Limitation:</strong> No unnecessary data retention</li>
                <li><strong>Accuracy:</strong> Data processed is accurate and up-to-date</li>
                <li><strong>Security:</strong> Appropriate security measures are implemented</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>3. Personal Data We Collect</h2>
              
              <div className="privacy-subsection">
                <h3>3.1 Personal Data We DO NOT Collect</h3>
                <p><strong>Privacy by Design:</strong> UtilityTools PDF Converter is designed to minimize data collection:</p>
                <div className="data-list no-collect">
                  <div className="data-item">❌ <strong>No Personal Identifiers:</strong> Name, email, phone number, address</div>
                  <div className="data-item">❌ <strong>No Biometric Data:</strong> Fingerprints, facial recognition, voice data</div>
                  <div className="data-item">❌ <strong>No Financial Data:</strong> Banking details, payment information</div>
                  <div className="data-item">❌ <strong>No Sensitive Personal Data:</strong> As defined under DPDP Act 2023</div>
                  <div className="data-item">❌ <strong>No Behavioral Tracking:</strong> Usage patterns, analytics, profiling</div>
                  <div className="data-item">❌ <strong>No Location Data:</strong> GPS coordinates, location history</div>
                  <div className="data-item">❌ <strong>No Device Identifiers:</strong> IMEI, MAC address, unique device IDs</div>
                </div>
              </div>

              <div className="privacy-subsection">
                <h3>3.2 Technical Data (Non-Personal)</h3>
                <p>The App may process the following non-personal technical data locally:</p>
                <ul className="privacy-list">
                  <li><strong>File Metadata:</strong> File names, sizes, formats (only for conversion purposes)</li>
                  <li><strong>Temporary Processing Data:</strong> Required for PDF conversion functionality</li>
                  <li><strong>App Performance Data:</strong> Crash logs stored locally (not transmitted)</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2>4. Legal Basis for Processing (DPDP Act 2023)</h2>
              <p>Our legal basis for processing under DPDP Act 2023:</p>
              <ol className="privacy-list">
                <li><strong>Legitimate Use:</strong> Providing PDF conversion services you explicitly request</li>
                <li><strong>Consent:</strong> When you grant device permissions for file access</li>
                <li><strong>Performance of Function:</strong> Core functionality of PDF conversion</li>
              </ol>
            </section>

            <section className="privacy-section">
              <h2>5. How We Use Data</h2>
              
              <div className="privacy-subsection">
                <h3>5.1 Primary Purposes</h3>
                <ul className="privacy-list">
                  <li>Converting files to PDF format in your browser</li>
                  <li>Downloading converted files to your local storage</li>
                  <li>Providing file sharing functionality through browser capabilities</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3>5.2 No Secondary Uses</h3>
                <ul className="privacy-list">
                  <li>No marketing or promotional activities</li>
                  <li>No profiling or automated decision-making</li>
                  <li>No data monetization or third-party sharing</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2>6. Data Storage and Security</h2>
              
              <div className="privacy-subsection">
                <h3>6.1 Browser-Only Processing</h3>
                <ul className="privacy-list">
                  <li>All processing occurs in your browser</li>
                  <li>No data transmission to external servers</li>
                  <li>No cloud storage or remote backup</li>
                  <li>Compliance with data localization requirements</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3>6.2 Security Measures</h3>
                <ul className="privacy-list">
                  <li><strong>Browser Security:</strong> Files processed in secure browser environment</li>
                  <li><strong>Local Processing:</strong> All conversion happens client-side</li>
                  <li><strong>Automatic Cleanup:</strong> Temporary data cleared after conversion</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3>6.3 Data Retention</h3>
                <ul className="privacy-list">
                  <li><strong>No Permanent Storage:</strong> Files not stored beyond conversion process</li>
                  <li><strong>User Control:</strong> All data remains under your complete control</li>
                  <li><strong>Browser Data:</strong> Clearing browser data removes all traces</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2>7. Your Rights Under DPDP Act 2023</h2>
              <div className="rights-grid">
                <div className="right-item">
                  <h4>Right to Information</h4>
                  <p>This privacy policy provides complete information about our practices</p>
                </div>
                <div className="right-item">
                  <h4>Right of Access</h4>
                  <p>Since we don't collect personal data, no access requests are necessary</p>
                </div>
                <div className="right-item">
                  <h4>Right to Correction</h4>
                  <p>Files you process remain under your control for any corrections</p>
                </div>
                <div className="right-item">
                  <h4>Right to Erasure</h4>
                  <p>Clear your browser data to completely remove all traces</p>
                </div>
              </div>
            </section>

            <section className="privacy-section">
              <h2>8. Children's Data Protection</h2>
              <ul className="privacy-list">
                <li><strong>Age Restriction:</strong> App suitable for users of all ages</li>
                <li><strong>No Collection:</strong> We don't collect data from children or adults</li>
                <li><strong>Parental Control:</strong> Parents can supervise app usage as it's completely browser-based</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>9. Technical Safeguards</h2>
              
              <div className="privacy-subsection">
                <h3>9.1 Web App Architecture</h3>
                <ul className="privacy-list">
                  <li><strong>Client-Side Processing:</strong> All conversion happens in your browser</li>
                  <li><strong>No Server Communication:</strong> No data sent to external servers</li>
                  <li><strong>Browser Sandbox:</strong> App operates in secure browser environment</li>
                </ul>
              </div>

              <div className="privacy-subsection">
                <h3>9.2 Data Protection by Design</h3>
                <ul className="privacy-list">
                  <li><strong>Zero Collection:</strong> No personal data collected</li>
                  <li><strong>Purpose Binding:</strong> Files processed only for PDF conversion</li>
                  <li><strong>Automatic Cleanup:</strong> Temporary data cleared automatically</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2>10. Business Model Transparency</h2>
              <ul className="privacy-list">
                <li><strong>Service Nature:</strong> Free web-based PDF conversion service</li>
                <li><strong>No Revenue Model:</strong> No monetization, advertising, or data collection for profit</li>
                <li><strong>No Advertising:</strong> No ad networks or tracking pixels</li>
                <li><strong>No Subscriptions:</strong> No paid services or ongoing data collection</li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>11. Contact Information</h2>
              <div className="contact-info">
                <p><strong>Data Controller:</strong> nkcodescribbler</p>
                <p><strong>Email:</strong> will.rise.concern@gmail.com</p>
                
                <div className="contact-details">
                  <h4>For Privacy Concerns or Queries:</h4>
                  <ul>
                    <li><strong>Response Time:</strong> As available (non-profit service)</li>
                    <li><strong>Languages:</strong> English</li>
                    <li><strong>Note:</strong> This is a free service. Contact for genuine concerns only.</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="privacy-section acknowledgment">
              <h2>Acknowledgment</h2>
              <p>
                By using UtilityTools PDF Converter, you acknowledge that you have read, understood, and agree to be 
                bound by this Privacy Policy and our commitment to protecting your privacy under Indian law.
              </p>
              
              <div className="review-dates">
                <p><strong>Last Review Date:</strong> Sep 10, 2025</p>
                <p><strong>Next Review Date:</strong> Sep 10, 2026</p>
              </div>
              
              <p className="compliance-note">
                <em>This Privacy Policy is drafted in compliance with the Digital Personal Data Protection Act 2023, 
                Information Technology Act 2000, and other applicable Indian privacy laws.</em>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;